import React, { useState } from "react";
import { toast } from "sonner";
import { useHQAuth } from "@/hq/HQAuthContext";
import { formatApiError } from "@/hq/hqApi";
import HQBrand from "@/hq/HQBrand";
import "@/hq/hq-forms.css";

export default function HQLogin() {
  const { login } = useHQAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim(), password);
      toast.success("Signed in to Orange HQ");
    } catch (err) {
      const msg = formatApiError(err?.response?.data?.detail) || "Login failed";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-hq-scope="true" className="min-h-screen grid grid-cols-1 md:grid-cols-2 bg-[#FFFDF6] text-[#0A0A0A]"
         style={{ fontFamily: "'Manrope', ui-sans-serif, system-ui, -apple-system, sans-serif" }}>
      {/* LEFT — brand panel */}
      <div className="relative hidden md:flex flex-col justify-between p-12 text-white overflow-hidden"
           style={{ background: "radial-gradient(ellipse 120% 100% at 30% 30%, #FFB347 0%, #FF8A2E 30%, #FF7A00 55%, #FF6A00 80%, #D94C00 100%)" }}>
        <div>
          <div className="text-[11px] tracking-[0.32em] uppercase font-bold text-black/70">Private</div>
          <div className="mt-4">
            <HQBrand theme="orange" size="lg" testId="hq-login-brand" />
          </div>
          <div className="mt-4 text-black font-black text-sm tracking-[0.24em] uppercase">
            Control Centre
          </div>
        </div>
        <div>
          <div className="text-[11px] tracking-[0.28em] uppercase font-bold text-black/70">Your business</div>
          <div className="mt-1 font-black text-3xl tracking-[-0.01em]">One system.</div>
        </div>
      </div>

      {/* RIGHT — form */}
      <div className="flex flex-col justify-center px-6 md:px-14 py-14">
        <div className="max-w-[420px] w-full">
          <div className="text-[11px] tracking-[0.32em] uppercase font-bold text-[#8A8A8A]">Sign in</div>
          <div className="mt-2 md:hidden">
            <HQBrand theme="light" size="md" testId="hq-login-brand-mobile" />
          </div>
          <h1 className="mt-2 font-black text-4xl tracking-[-0.02em] hidden md:block">Sign in to HQ</h1>
          <p className="mt-3 text-[15px] text-[#4A4A4A]">
            Admins only. This area is not part of the public OrangeBySite website.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="hq-login-form">
            <label className="block">
              <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#0A0A0A] mb-2">Email or username</div>
              <input
                type="text"
                required
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="hq-login-email"
                className="w-full px-4 py-3 rounded-xl border border-[#EAEAEA] bg-white text-[15px] focus:outline-none focus:ring-2 focus:ring-[#FF6A00] focus:border-[#FF6A00]"
                placeholder=""
              />
            </label>
            <label className="block">
              <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#0A0A0A] mb-2">Password</div>
              <input
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="hq-login-password"
                className="w-full px-4 py-3 rounded-xl border border-[#EAEAEA] bg-white text-[15px] focus:outline-none focus:ring-2 focus:ring-[#FF6A00] focus:border-[#FF6A00]"
              />
            </label>

            <button
              type="submit"
              disabled={busy || !email || !password}
              data-testid="hq-login-submit"
              className="w-full mt-3 rounded-full bg-[#0A0A0A] text-white py-4 text-[12px] font-black tracking-[0.24em] uppercase hover:bg-[#1A1A1A] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="mt-10 text-[11px] tracking-[0.24em] uppercase text-[#8A8A8A]">
            <a href="/" className="hover:text-[#0A0A0A]">← Back to public site</a>
          </div>
        </div>
      </div>
    </div>
  );
}
